import React, {
  useCallback,
  useEffect,
  useState
} from "react";

import { useNavigate } from "react-router-dom";

const API = "https://live-evo.com/api/agencies";
const CYAN = "#00f2ff";

function authHeaders(withJson = false) {
  const token = localStorage.getItem("evoAccessToken");

  return {
    ...(withJson
      ? {"Content-Type": "application/json"}
      : {}),
    Authorization: `Bearer ${token || ""}`
  };
}

function parseList(value) {
  if (Array.isArray(value)) {
    return value;
  }

  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function mediaUrl(value) {
  const url = String(value || "").trim();

  if (!url) {
    return "";
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return `https://live-evo.com${
    url.startsWith("/") ? url : `/${url}`
  }`;
}

async function fetchWithSession(url, options = {}) {
  let response = await fetch(url, options);

  if (response.status !== 401) {
    return response;
  }

  const oldToken =
    localStorage.getItem("evoAccessToken");

  if (!oldToken) {
    return response;
  }

  const refreshResponse = await fetch(
    "https://live-evo.com/api/auth/refresh",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${oldToken}`
      }
    }
  );

  const refreshData = await refreshResponse.json();

  if (
    !refreshResponse.ok ||
    !refreshData.accessToken
  ) {
    return response;
  }

  localStorage.setItem(
    "evoAccessToken",
    refreshData.accessToken
  );

  const renewedOptions = {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization:
        `Bearer ${refreshData.accessToken}`
    }
  };

  return fetch(url, renewedOptions);
}

function StaffAgenciesPage() {
  const navigate = useNavigate();

  const [agencies, setAgencies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");

  const requestedLevel = Number(
    selected?.reviews?.find(
      review => review.review_type === "creation"
    )?.requested_level || 0
  );

  const loadAgencies = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetchWithSession(
        `${API}/staff/pending`,
        {headers: authHeaders()}
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || "Chargement impossible"
        );
      }

      setAgencies(
        Array.isArray(data.agencies)
          ? data.agencies
          : []
      );
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  const openAgency = async agency => {
    setDetailsLoading(true);
    setError("");
    setReason("");

    try {
      const response = await fetchWithSession(
        `${API}/staff/${agency.id}`,
        {headers: authHeaders()}
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || "Dossier indisponible"
        );
      }

      setSelected(data);
    } catch (detailsError) {
      setError(detailsError.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  const decide = async action => {
    if (
      action !== "approve" &&
      reason.trim().length < 10
    ) {
      setError(
        "Explique la décision avec au moins 10 caractères."
      );
      return;
    }

    setProcessing(true);
    setError("");

    try {
      const response = await fetchWithSession(
        `${API}/staff/${selected.agency.id}/decision`,
        {
          method: "POST",
          headers: authHeaders(true),
          body: JSON.stringify({
            action,
            reason: reason.trim()
          })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || "Décision impossible"
        );
      }

      setSelected(null);
      setReason("");
      await loadAgencies();
    } catch (decisionError) {
      setError(decisionError.message);
    } finally {
      setProcessing(false);
    }
  };

  const statusLabel = status => {
    if (status === "changes_requested") {
      return "Corrections demandées";
    }

    return "En attente";
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <button
            onClick={() => navigate("/staff")}
            style={styles.backButton}
          >
            ‹
          </button>

          <div>
            <div style={styles.eyebrow}>
              CENTRE STAFF EVO
            </div>
            <h1 style={styles.title}>
              🏢 Demandes d’agences
            </h1>
            <p style={styles.subtitle}>
              Contrôle, certification et niveaux de bonus
            </p>
          </div>
        </div>

        {error && (
          <div style={styles.error}>{error}</div>
        )}

        <div style={styles.summary}>
          <div style={styles.summaryValue}>
            {agencies.length}
          </div>
          <div>
            <strong>Dossiers à examiner</strong>
            <div style={styles.muted}>
              Aucune validation n’est automatique
            </div>
          </div>
        </div>

        {loading ? (
          <div style={styles.empty}>
            Chargement des demandes…
          </div>
        ) : agencies.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>✓</div>
            <h3>Aucune demande en attente</h3>
            <p style={styles.muted}>
              Tous les dossiers ont été traités.
            </p>
          </div>
        ) : (
          <div style={styles.grid}>
            {agencies.map(agency => (
              <button
                key={agency.id}
                onClick={() => openAgency(agency)}
                style={styles.card}
              >
                <div style={styles.cardTop}>
                  <div style={styles.logo}>
                    {agency.logo_url ? (
                      <img
                        src={agency.logo_url}
                        alt=""
                        style={styles.logoImage}
                      />
                    ) : (
                      String(agency.name || "A")
                        .slice(0, 1)
                        .toUpperCase()
                    )}
                  </div>

                  <div style={{flex: 1}}>
                    <div style={styles.status}>
                      {statusLabel(agency.status)}
                    </div>
                    <div style={styles.cardTitle}>
                      {agency.name}
                    </div>
                    <div style={styles.muted}>
                      Président :{" "}
                      {agency.president_pseudo ||
                        "Utilisateur EVO"}
                    </div>
                  </div>
                </div>

                <p style={styles.description}>
                  {agency.description}
                </p>

                <div style={styles.tags}>
                  {(agency.specialties || [])
                    .slice(0, 4)
                    .map(item => (
                      <span
                        key={item}
                        style={styles.tag}
                      >
                        {item}
                      </span>
                    ))}
                </div>

                <div style={styles.openText}>
                  Ouvrir le dossier ›
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {(selected || detailsLoading) && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            {detailsLoading ? (
              <div style={styles.empty}>
                Chargement du dossier…
              </div>
            ) : (
              <>
                <button
                  onClick={() => setSelected(null)}
                  style={styles.close}
                >
                  ×
                </button>

                <div style={styles.eyebrow}>
                  DOSSIER #{selected.agency.id}
                </div>

                <div style={styles.modalHero}>
                  <div style={styles.modalLogo}>
                    {selected.agency.logo_url ? (
                      <img
                        src={mediaUrl(
                          selected.agency.logo_url
                        )}
                        alt={`Logo ${selected.agency.name}`}
                        style={styles.modalLogoImage}
                      />
                    ) : (
                      String(selected.agency.name || "A")
                        .slice(0, 1)
                        .toUpperCase()
                    )}
                  </div>

                  <div style={styles.modalHeroBody}>
                    <h2 style={styles.modalTitle}>
                      {selected.agency.name}
                    </h2>
                    <div style={styles.status}>
                      {statusLabel(selected.agency.status)}
                    </div>
                    <p style={styles.description}>
                      {selected.agency.description}
                    </p>
                  </div>
                </div>

                <div style={styles.infoGrid}>
                  <div style={styles.infoBox}>
                    <span style={styles.muted}>
                      Président
                    </span>
                    <strong>
                      {selected.agency.president_pseudo ||
                        "Utilisateur EVO"}
                    </strong>
                  </div>

                  <div style={styles.infoBox}>
                    <span style={styles.muted}>
                      Statut du compte
                    </span>
                    <strong>
                      {selected.agency.president_status ||
                        "Non renseigné"}
                    </strong>
                  </div>

                  <div style={styles.infoBox}>
                    <span style={styles.muted}>
                      Capacité maximale
                    </span>
                    <strong>
                      {selected.agency.member_limit} membres
                    </strong>
                  </div>

                  <div style={styles.infoBox}>
                    <span style={styles.muted}>
                      Pack demandé
                    </span>
                    <strong style={{color: CYAN}}>
                      {requestedLevel || "—"} %
                    </strong>
                  </div>

                  <div style={styles.infoBox}>
                    <span style={styles.muted}>
                      Signatures enregistrées
                    </span>
                    <strong>
                      {selected.acceptances.length}
                    </strong>
                  </div>

                  <div style={styles.infoBox}>
                    <span style={styles.muted}>
                      Contrôles précédents
                    </span>
                    <strong>
                      {selected.reviews.length}
                    </strong>
                  </div>
                </div>

                <h3 style={styles.sectionTitle}>
                  Projet et objectifs
                </h3>
                <div style={styles.detailCard}>
                  <p style={styles.detailText}>
                    {selected.agency.objectives ||
                      "Objectifs non renseignés"}
                  </p>
                </div>

                <h3 style={styles.sectionTitle}>
                  Critères de recrutement
                </h3>
                <div style={styles.detailCard}>
                  <p style={styles.detailText}>
                    {selected.agency.recruitment_criteria ||
                      "Critères non renseignés"}
                  </p>
                </div>

                <h3 style={styles.sectionTitle}>
                  Expérience du président
                </h3>
                <div style={styles.detailCard}>
                  <p style={styles.detailText}>
                    {selected.agency.president_experience ||
                      "Expérience non renseignée"}
                  </p>
                </div>

                <h3 style={styles.sectionTitle}>
                  Spécialités
                </h3>
                <div style={styles.tags}>
                  {parseList(
                    selected.agency.specialties_json
                  ).map(item => (
                    <span key={item} style={styles.tag}>
                      {item}
                    </span>
                  ))}
                </div>

                <h3 style={styles.sectionTitle}>
                  Services proposés
                </h3>
                <div style={styles.tags}>
                  {parseList(
                    selected.agency.services_json
                  ).map(item => (
                    <span key={item} style={styles.tag}>
                      {item}
                    </span>
                  ))}
                </div>

                <h3 style={styles.sectionTitle}>
                  Preuve du règlement
                </h3>

                {selected.acceptances.length === 0 ? (
                  <div style={styles.warning}>
                    Aucune signature enregistrée
                  </div>
                ) : (
                  selected.acceptances.map(item => (
                    <div
                      key={item.id}
                      style={styles.proof}
                    >
                      <strong>{item.title}</strong>
                      <span style={styles.muted}>
                        Version {item.version}
                      </span>
                      <span style={styles.muted}>
                        Signé par {item.signer_name}
                      </span>
                      <code style={styles.hash}>
                        {item.rule_hash}
                      </code>
                    </div>
                  ))
                )}

                <h3 style={styles.sectionTitle}>
                  Pack demandé par l’agence
                </h3>

                <div style={styles.levels}>
                  <div
                    style={{
                      ...styles.level,
                      ...styles.levelActive,
                      cursor: "default"
                    }}
                  >
                    {requestedLevel || "—"} %
                  </div>
                </div>

                <div style={styles.warning}>
                  Le Staff vérifie les conditions du dossier. Il peut
                  approuver ce pack, demander des corrections ou refuser
                  la demande, mais il ne peut pas choisir un autre taux.
                </div>

                <textarea
                  value={reason}
                  onChange={event =>
                    setReason(event.target.value)
                  }
                  placeholder="Note Staff ou motif obligatoire pour correction/refus…"
                  style={styles.textarea}
                />

                <div style={styles.actions}>
                  <button
                    disabled={processing}
                    onClick={() =>
                      decide("changes_requested")
                    }
                    style={styles.correctionButton}
                  >
                    Demander une correction
                  </button>

                  <button
                    disabled={processing}
                    onClick={() => decide("reject")}
                    style={styles.rejectButton}
                  >
                    Refuser
                  </button>

                  <button
                    disabled={processing}
                    onClick={() => decide("approve")}
                    style={styles.approveButton}
                  >
                    Approuver le pack demandé{" "}
                    {requestedLevel || "—"} %
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top, #06223a 0%, #01050c 38%, #000 100%)",
    color: "#fff",
    padding: "28px 16px 60px"
  },
  container: {
    width: "min(1180px, 100%)",
    margin: "0 auto"
  },
  header: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    marginBottom: "26px"
  },
  backButton: {
    width: "46px",
    height: "46px",
    borderRadius: "50%",
    border: `1px solid ${CYAN}`,
    background: "#04111f",
    color: "#fff",
    fontSize: "30px",
    cursor: "pointer"
  },
  eyebrow: {
    color: CYAN,
    fontSize: "11px",
    fontWeight: "900",
    letterSpacing: "2px"
  },
  title: {
    margin: "4px 0",
    color: "#fff",
    fontSize: "clamp(25px, 5vw, 38px)"
  },
  subtitle: {
    margin: 0,
    color: "#8fa7bd"
  },
  summary: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    padding: "18px",
    marginBottom: "22px",
    border: "1px solid rgba(0,242,255,.45)",
    borderRadius: "20px",
    background: "rgba(3,17,31,.88)"
  },
  summaryValue: {
    width: "54px",
    height: "54px",
    borderRadius: "18px",
    display: "grid",
    placeItems: "center",
    background: CYAN,
    color: "#001018",
    fontSize: "24px",
    fontWeight: "900"
  },
  grid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(270px, 1fr))",
    gap: "16px"
  },
  card: {
    width: "100%",
    padding: "18px",
    border: "1px solid rgba(0,242,255,.6)",
    borderRadius: "22px",
    background:
      "linear-gradient(145deg, #061a2c, #030912)",
    color: "#fff",
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "0 0 20px rgba(0,242,255,.12)"
  },
  cardTop: {
    display: "flex",
    gap: "13px",
    alignItems: "center"
  },
  logo: {
    width: "58px",
    height: "58px",
    flex: "0 0 58px",
    borderRadius: "18px",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    border: `1px solid ${CYAN}`,
    background: "#09233d",
    color: CYAN,
    fontSize: "24px",
    fontWeight: "900"
  },
  logoImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  status: {
    color: CYAN,
    fontSize: "10px",
    fontWeight: "900",
    textTransform: "uppercase"
  },
  cardTitle: {
    fontSize: "19px",
    fontWeight: "900",
    margin: "3px 0"
  },
  description: {
    color: "#abc0d3",
    lineHeight: 1.55
  },
  muted: {
    color: "#8299ad",
    fontSize: "12px"
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "7px"
  },
  tag: {
    padding: "5px 9px",
    borderRadius: "12px",
    color: "#bffbff",
    background: "#08243a",
    fontSize: "11px"
  },
  openText: {
    marginTop: "15px",
    color: CYAN,
    fontWeight: "800",
    textAlign: "right"
  },
  empty: {
    padding: "60px 20px",
    textAlign: "center",
    border: "1px solid #163346",
    borderRadius: "24px",
    background: "#030a12"
  },
  emptyIcon: {
    color: CYAN,
    fontSize: "40px"
  },
  error: {
    padding: "13px",
    marginBottom: "15px",
    borderRadius: "13px",
    color: "#ffd6dc",
    background: "#4b1020",
    border: "1px solid #ff4d6d"
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    padding: "18px",
    overflowY: "auto",
    background: "rgba(0,0,0,.88)"
  },
  modal: {
    position: "relative",
    width: "min(850px, 100%)",
    margin: "20px auto",
    padding: "clamp(20px, 4vw, 34px)",
    borderRadius: "26px",
    background:
      "linear-gradient(150deg, #071c30, #02060d)",
    border: `1px solid ${CYAN}`,
    boxShadow: "0 0 30px rgba(0,242,255,.25)"
  },
  close: {
    position: "absolute",
    top: "14px",
    right: "14px",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "1px solid #456",
    background: "#050a10",
    color: "#fff",
    fontSize: "25px",
    cursor: "pointer"
  },
  modalHero: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "18px",
    marginTop: "12px",
    padding: "18px",
    borderRadius: "20px",
    background:
      "linear-gradient(135deg, #08233b, #100d2b)",
    border: "1px solid rgba(0,242,255,.45)"
  },
  modalLogo: {
    width: "96px",
    height: "96px",
    flex: "0 0 96px",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    borderRadius: "26px",
    border: `1px solid ${CYAN}`,
    background: "#09233d",
    color: CYAN,
    fontSize: "38px",
    fontWeight: "900",
    boxShadow: "0 0 20px rgba(0,242,255,.25)"
  },
  modalLogoImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  modalHeroBody: {
    flex: "1 1 300px",
    minWidth: 0
  },
  modalTitle: {
    margin: "7px 45px 10px 0",
    fontSize: "28px",
    overflowWrap: "anywhere"
  },
  detailCard: {
    padding: "15px",
    borderRadius: "16px",
    background: "#061421",
    border: "1px solid #19445c"
  },
  detailText: {
    margin: 0,
    color: "#c3d4e3",
    lineHeight: 1.65,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere"
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "10px",
    margin: "20px 0"
  },
  infoBox: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    padding: "13px",
    borderRadius: "14px",
    background: "#071421",
    border: "1px solid #17394d"
  },
  sectionTitle: {
    marginTop: "24px",
    fontSize: "17px",
    color: "#fff"
  },
  proof: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "14px",
    marginBottom: "9px",
    borderRadius: "15px",
    background: "#04121f",
    border: "1px solid #14506b"
  },
  hash: {
    overflowWrap: "anywhere",
    color: CYAN,
    fontSize: "10px"
  },
  warning: {
    padding: "13px",
    color: "#ffd27a",
    background: "#392b09",
    borderRadius: "13px"
  },
  levels: {
    display: "flex",
    gap: "10px"
  },
  level: {
    flex: 1,
    padding: "13px",
    borderRadius: "15px",
    border: "1px solid #31516a",
    background: "#07111b",
    color: "#fff",
    fontWeight: "900",
    cursor: "pointer"
  },
  levelActive: {
    borderColor: CYAN,
    background: "#06334b",
    color: CYAN,
    boxShadow: "0 0 14px rgba(0,242,255,.25)"
  },
  textarea: {
    width: "100%",
    minHeight: "110px",
    marginTop: "18px",
    padding: "14px",
    boxSizing: "border-box",
    resize: "vertical",
    borderRadius: "15px",
    border: "1px solid #2a526b",
    background: "#02070d",
    color: "#fff"
  },
  actions: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "10px",
    marginTop: "16px"
  },
  correctionButton: {
    padding: "13px",
    borderRadius: "14px",
    border: "1px solid #ffbd4a",
    background: "#3b2908",
    color: "#ffd079",
    fontWeight: "800",
    cursor: "pointer"
  },
  rejectButton: {
    padding: "13px",
    borderRadius: "14px",
    border: "1px solid #ff496b",
    background: "#40101d",
    color: "#ff9bad",
    fontWeight: "800",
    cursor: "pointer"
  },
  approveButton: {
    padding: "13px",
    borderRadius: "14px",
    border: "none",
    background:
      "linear-gradient(90deg, #00eaff, #087cff)",
    color: "#001019",
    fontWeight: "900",
    cursor: "pointer"
  }
};

export default StaffAgenciesPage;
